import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PricingMode } from '@/lib/graphql/generated/graphql';
import {
  type PricingModeChoice,
  PricingModeRequest,
} from '@/types/pricing-mode-request';
import { ArrowLeft } from 'lucide-react';

/** Valeur du choix « je ne saisis pas de prix ». */
const SKIP_PRICES: PricingModeChoice = 'skip';

const I18N = 'components.import_flow.pricing_mode';

interface ModeOption {
  value: PricingModeChoice;
  label: string;
  description: string;
}

/**
 * Options selon le profil :
 * - Interne (Choose) : prix Choose+rétro (marge dérivée), ou marge (Choose+rétro
 *   dérivés), + aucun prix.
 * - Marque (externe) : prix de cession (Choose dérivé), + aucun prix.
 *
 * L'option « je ne saisis pas de prix » est toujours proposée : l'import peut
 * créer les produits sans renseigner de prix (le deal peut être vide au départ).
 */
const getModeOptions = (
  t: (key: string) => string,
  isInternalUser: boolean
): ModeOption[] => {
  const skip: ModeOption = {
    value: SKIP_PRICES,
    label: t(`${I18N}.skip_label`),
    description: t(`${I18N}.skip_description`),
  };

  const options: ModeOption[] = !isInternalUser
    ? [
        {
          value: PricingMode.BuyingPriceFixed,
          label: t(`${I18N}.brand_buying_price_fixed_label`),
          description: t(`${I18N}.brand_buying_price_fixed_description`),
        },
      ]
    : [
        {
          value: PricingMode.BuyingPriceFixed,
          label: t(`${I18N}.buying_price_fixed_label`),
          description: t(`${I18N}.buying_price_fixed_description`),
        },
        {
          value: PricingMode.MarginFixed,
          label: t(`${I18N}.margin_fixed_label`),
          description: t(`${I18N}.margin_fixed_description`),
        },
      ];

  return [...options, skip];
};

interface ViewProductsPricingModeProps {
  /** Requête en attente (pattern promise/callback, cf. PricingModeRequest). */
  request: PricingModeRequest | null;
  /** Profil : interne (Choose) → Choose+rétro / marge ; marque → cession. */
  isInternalUser: boolean;
  /** Confirme le choix : résout la requête puis laisse CSVBox s'ouvrir. */
  onConfirm: () => void;
  /** Revient à l'étape précédente (annule l'ouverture de CSVBox). */
  onBack: () => void;
}

/**
 * Étape du wizard d'import : choix de la base de saisie des prix.
 * Rendu DANS la Dialog d'import partagée (pas de modale séparée) pour une
 * transition fluide depuis le menu produits. Le wording et les options
 * s'adaptent au profil (interne Choose vs marque).
 */
export const ViewProductsPricingMode: React.FC<
  ViewProductsPricingModeProps
> = ({ request, isInternalUser, onConfirm, onBack }) => {
  const { t } = useTranslation();
  const options = getModeOptions(t, isInternalUser);

  // Marque : un seul mode de prix possible (cession = BuyingPriceFixed), donc
  // pré-sélectionné. Interne : on pré-remplit avec le mode actuel du deal.
  const defaultMode: PricingModeChoice = isInternalUser
    ? (request?.initialMode ?? PricingMode.MarginFixed)
    : PricingMode.BuyingPriceFixed;
  const [mode, setMode] = useState<PricingModeChoice>(defaultMode);

  useEffect(() => {
    if (request) {
      setMode(
        isInternalUser ? request.initialMode : PricingMode.BuyingPriceFixed
      );
    }
  }, [request, isInternalUser]);

  const handleConfirm = () => {
    request?.onDecision(mode);
    onConfirm();
  };

  const handleBack = () => {
    request?.onDecision(null);
    onBack();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={handleBack}
            aria-label={t('general.previous')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>{t(`${I18N}.title`)}</DialogTitle>
        </div>
      </DialogHeader>

      <div className="space-y-4 py-6">
        <RadioGroup
          value={mode}
          onValueChange={(value) => setMode(value as PricingModeChoice)}
          className="space-y-3"
        >
          {options.map((option) => (
            <Label
              key={option.value}
              htmlFor={`pricing-mode-${option.value}`}
              className="flex cursor-pointer items-start gap-3 rounded-md border px-3 py-3"
            >
              <RadioGroupItem
                id={`pricing-mode-${option.value}`}
                value={option.value}
                className="mt-0.5"
              />
              <span className="space-y-1">
                <span className="block text-sm font-medium leading-5">
                  {option.label}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {option.description}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleConfirm}>{t(`${I18N}.continue`)}</Button>
      </div>
    </>
  );
};
